import { useEffect, useState } from "react";
import { Cookie, Settings, X } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "revgrow.cookie.consent.v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [detailed, setDetailed] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const existing = readConsent();
    if (!existing) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  };
  const rejectAll = () => {
    writeConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };
  const saveChoice = () => {
    writeConsent(prefs);
    setVisible(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed left-4 right-4 bottom-4 md:left-6 md:right-6 md:bottom-6 z-[60] pointer-events-none"
    >
      <div className="pointer-events-auto max-w-3xl mx-auto glass rounded-2xl border border-white/[0.08] p-5 md:p-6 shadow-2xl">
        {!detailed ? (
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="h-10 w-10 rounded-lg bg-teal/10 border border-teal/25 grid place-items-center text-teal shrink-0">
              <Cookie size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-base md:text-lg leading-tight">
                Cookies on revgrow-services.co.uk
              </div>
              <p className="mt-1.5 text-xs md:text-sm text-muted-foreground leading-relaxed">
                We use cookies to run this site, measure how it{"\u2019"}s used, and
                improve your experience. You can accept all, reject non-essential, or
                pick and choose. See our{" "}
                <Link
                  to="/privacy"
                  className="text-teal hover:underline"
                  onClick={() => setVisible(false)}
                >
                  privacy policy
                </Link>
                .
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  data-testid="cookie-accept-all"
                  onClick={acceptAll}
                  className="rounded-full bg-teal text-[#050A0F] px-4 py-2 text-xs font-medium hover:opacity-90"
                >
                  Accept all
                </button>
                <button
                  data-testid="cookie-reject-all"
                  onClick={rejectAll}
                  className="rounded-full border border-white/15 px-4 py-2 text-xs hover:border-white/30"
                >
                  Reject non-essential
                </button>
                <button
                  data-testid="cookie-customise"
                  onClick={() => setDetailed(true)}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Settings size={13} /> Customise
                </button>
              </div>
            </div>
            <button
              aria-label="Dismiss"
              onClick={rejectAll}
              className="text-muted-foreground hover:text-foreground shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-semibold text-base md:text-lg">
                Cookie preferences
              </div>
              <button
                aria-label="Close"
                onClick={() => setDetailed(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3 mb-4">
              <ToggleRow
                title="Strictly necessary"
                desc="Required to run this site. Cannot be disabled."
                checked
                disabled
              />
              <ToggleRow
                title="Analytics"
                desc="Anonymous usage stats to help us improve the site."
                checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                testid="cookie-toggle-analytics"
              />
              <ToggleRow
                title="Marketing"
                desc="Personalise campaigns and measure their impact."
                checked={prefs.marketing}
                onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                testid="cookie-toggle-marketing"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={rejectAll}
                className="rounded-full border border-white/15 px-4 py-2 text-xs hover:border-white/30"
              >
                Reject non-essential
              </button>
              <button
                data-testid="cookie-save-choices"
                onClick={saveChoice}
                className="rounded-full bg-teal text-[#050A0F] px-4 py-2 text-xs font-medium hover:opacity-90"
              >
                Save choices
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-white/5 p-3 hover:border-white/10 transition">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        disabled={disabled}
        data-testid={testid}
        onClick={() => onChange && onChange(!checked)}
        className={`relative h-5 w-9 rounded-full transition ${
          checked ? "bg-teal" : "bg-white/10"
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
            checked ? "left-[18px]" : "left-0.5"
          }`}
        />
      </button>
    </label>
  );
}
